import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { presenceSocketUrl } from "../api";
import { acquireIdToken } from "../auth/token";
import type { PresenceRecord } from "../types";

const RETRY_MS = 2000;

/**
 * Live team roster over the BFF's presence WebSocket. Check-in is a
 * separate REST call (postCheckIn); this hook only reads the broadcast
 * roster and reconnects with a fixed backoff on drop.
 */
export function usePresence(eventKey: string): { presence: PresenceRecord[]; connected: boolean } {
  const { instance, accounts } = useMsal();
  const [presence, setPresence] = useState<PresenceRecord[]>([]);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (disposed) return;
      void acquireIdToken(instance)
        .then((token) => {
          if (disposed) return;
          socket = new WebSocket(presenceSocketUrl(eventKey, token));
          socket.onopen = () => setConnected(true);
          socket.onmessage = (event) => {
            try {
              const payload = JSON.parse(event.data) as { type?: string; results?: PresenceRecord[] };
              if (payload?.type === "presence" && Array.isArray(payload.results)) {
                setPresence(payload.results);
              }
            } catch {
              // Ignore malformed frames.
            }
          };
          socket.onclose = () => {
            setConnected(false);
            if (!disposed) {
              retryTimer = setTimeout(connect, RETRY_MS);
            }
          };
          socket.onerror = () => socket?.close();
        })
        .catch(() => {
          if (!disposed) {
            retryTimer = setTimeout(connect, RETRY_MS);
          }
        });
    };

    connect();
    return () => {
      disposed = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
    };
  }, [accounts, eventKey, instance]);

  return { presence, connected };
}
